/**
 * Admin (admin only):
 *  - Membership: isMember, isActive (PATCH {id}/status), member type and member code (PUT {id}).
 *  - Merge another record INTO this one (POST {id}/merge/{otherId}, Member.Delete): side-by-side
 *    preview of what is kept and filled, counts of what moves, then the list of other modules the
 *    API cannot rewrite, as a checklist.
 *  - Soft delete (DELETE {id}; refused while a login is linked) and restore (POST {id}/restore).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  IconDeviceFloppy, IconAlertCircle, IconGitMerge, IconTrash, IconRestore, IconAlertTriangle, IconArrowRight, IconCheck,
} from '@tabler/icons-react';
import {
  Card, Stack, Group, Button, Text, Alert, Select, TextInput, Switch, SimpleGrid, Modal, Checkbox, Badge, Divider, Skeleton, Table,
} from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useMemberService, type Member, type MemberMergeResult, type UpdateMemberRequest } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { useMemberTypes, useMaritalStatuses } from '../useMemberLookups';
import { useInvalidateMember } from '../useMemberMutations';
import { MemberSearchSelect } from '../MemberSearchSelect';
import { errorMessage, formatDob, memberFullName, textOrNull, formatStampTime } from '../memberFormat';

const blank = (v: unknown) => v === null || v === undefined || (typeof v === 'string' && !v.trim()) || v === 0;

export function AdminTab({ api, member, perms }: MemberTabProps) {
  const navigate = useNavigate();
  const memberService = useMemberService();
  const invalidate = useInvalidateMember(api);
  const { options: typeOptions, types } = useMemberTypes();
  const { statuses } = useMaritalStatuses();
  const name = memberFullName(member) || `Member ${member.memberID}`;
  const isDeleted = !!member.isDeleted;

  // ---------- Membership settings ----------
  const initial = useMemo(() => ({
    isMember: member.isMember,
    isActive: member.isActive,
    typeID: member.typeID > 0 ? String(member.typeID) : null,
    memberCode: member.memberCode ?? '',
  }), [member]);
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);
  const dirty = form.isMember !== initial.isMember || form.isActive !== initial.isActive || form.typeID !== initial.typeID
    || form.memberCode.trim() !== initial.memberCode.trim();

  const saveSettings = useMutation({
    mutationFn: async () => {
      const patch: UpdateMemberRequest = {};
      if (form.isMember !== initial.isMember) patch.isMember = form.isMember;
      if (form.typeID !== initial.typeID) patch.typeID = form.typeID ? Number(form.typeID) : null;
      if (form.memberCode.trim() !== initial.memberCode.trim()) patch.memberCode = textOrNull(form.memberCode);
      if (Object.keys(patch).length > 0) await memberService.update(member.memberID, patch);
      if (form.isActive !== initial.isActive) await memberService.updateStatus(member.memberID, form.isActive);
    },
    onSuccess: () => { toast.success('Saved', 'Membership settings updated.'); invalidate(); },
    onError: (e) => toast.error('Could not save', errorMessage(e)),
  });

  // ---------- Merge ----------
  const [other, setOther] = useState<Member | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [result, setResult] = useState<MemberMergeResult | null>(null);
  const [checked, setChecked] = useState<string[]>([]);

  const otherCounts = useQuery({
    queryKey: ['members', 'merge-preview', other?.memberID],
    enabled: !!other && mergeOpen,
    queryFn: async () => {
      const id = other!.memberID;
      const settle = <T,>(p: Promise<T[]>) => p.then((r) => r.length).catch(() => null);
      const [addresses, allergies, disabilities, families, locations, notes, emergency] = await Promise.all([
        settle(memberService.getMemberAddresses(id)),
        settle(memberService.getMemberAllergies(id)),
        settle(memberService.getMemberDisabilities(id)),
        settle(memberService.getMemberFamilies(id)),
        settle(memberService.getMemberLocations(id)),
        settle(memberService.getNotes(id)),
        settle(memberService.getEmergencyContacts(id)),
      ]);
      return { addresses, allergies, disabilities, families, locations, notes, emergency };
    },
  });

  const bothHaveLogins = !!other && !!member.userId && !!other.userId && member.userId !== other.userId;

  const typeName = (id?: number | null) => types.find((t) => t.typeID === id)?.name ?? (id ? `Type ${id}` : '');
  const maritalName = (id?: number | null) => statuses.find((s) => s.maritalStatusID === id)?.name ?? '';
  const previewRows: { label: string; mine: string; theirs: string }[] = other ? [
    { label: 'First name', mine: member.firstName ?? '', theirs: other.firstName ?? '' },
    { label: 'Middle name', mine: member.middleName ?? '', theirs: other.middleName ?? '' },
    { label: 'Surname', mine: member.lastName ?? '', theirs: other.lastName ?? '' },
    { label: 'Date of birth', mine: member.dateOfBirth ? formatDob(member.dateOfBirth) : '', theirs: other.dateOfBirth ? formatDob(other.dateOfBirth) : '' },
    { label: 'Gender', mine: member.gender ?? '', theirs: other.gender ?? '' },
    { label: 'Marital status', mine: member.maritalStatusID > 0 ? maritalName(member.maritalStatusID) : '', theirs: other.maritalStatusID > 0 ? maritalName(other.maritalStatusID) : '' },
    { label: 'Email', mine: member.email ?? '', theirs: other.email ?? '' },
    { label: 'Cellphone number', mine: member.cellNumber ?? '', theirs: other.cellNumber ?? '' },
    { label: 'Telephone', mine: member.telephone ?? '', theirs: other.telephone ?? '' },
    { label: 'Alternative contact', mine: member.altContact ?? '', theirs: other.altContact ?? '' },
    { label: 'Member code', mine: member.memberCode ?? '', theirs: other.memberCode ?? '' },
    { label: 'Member type', mine: typeName(member.typeID), theirs: typeName(other.typeID) },
    { label: 'Login', mine: member.userId ? 'Linked' : '', theirs: other.userId ? 'Linked' : '' },
    { label: 'Planning Centre', mine: member.planningCentrePersonId ? 'Linked' : '', theirs: other.planningCentrePersonId ? 'Linked' : '' },
    { label: 'Anatomy', mine: member.anatomyUserId ? 'Linked' : '', theirs: other.anatomyUserId ? 'Linked' : '' },
  ] : [];

  const merge = useMutation({
    mutationFn: () => memberService.merge(member.memberID, other!.memberID),
    onSuccess: (r) => {
      setResult(r);
      setChecked([]);
      setMergeOpen(false);
      setOther(null);
      setUnderstood(false);
      toast.success('Records merged', `Member ${r.mergedMemberID} was folded into ${name}.`);
      invalidate('all');
    },
    onError: (e) => toast.error('Could not merge', errorMessage(e)),
  });

  // ---------- Delete / restore ----------
  const [deleteOpen, setDeleteOpen] = useState(false);
  const softDelete = useMutation({
    mutationFn: () => memberService.softDelete(member.memberID),
    onSuccess: () => { toast.success('Member deleted', `${name} is hidden from the directory. You can restore the record here.`); setDeleteOpen(false); invalidate('all'); },
    onError: (e) => toast.error('Could not delete', errorMessage(e)),
  });
  const restore = useMutation({
    mutationFn: () => memberService.restore(member.memberID),
    onSuccess: () => { toast.success('Member restored', `${name} is back in the directory.`); invalidate('all'); },
    onError: (e) => toast.error('Could not restore', errorMessage(e)),
  });

  const count = (n: number | null | undefined) => (n === null || n === undefined ? 'unknown' : String(n));

  return (
    <Stack gap="md">
      {/* Membership */}
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <div>
            <Text fw={600}>Membership</Text>
            <Text size="sm" c="dimmed">Member or visitor, active or not, and how this person is classified.</Text>
          </div>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select label="Member type" placeholder="Choose a type" data={typeOptions} value={form.typeID} clearable
              disabled={!perms.editAdmin || isDeleted} onChange={(v) => setForm({ ...form, typeID: v })} />
            <TextInput label="Member code" value={form.memberCode} disabled={!perms.editAdmin || isDeleted}
              onChange={(e) => setForm({ ...form, memberCode: e.currentTarget.value })} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Switch label="Church member" description="Off means a visitor or someone not yet in membership." checked={form.isMember}
              disabled={!perms.editAdmin || isDeleted} onChange={(e) => setForm({ ...form, isMember: e.currentTarget.checked })} />
            <Switch label="Active" description="Inactive people are left out of Care, Courses, Tasks and notifications." checked={form.isActive}
              disabled={!perms.editAdmin || isDeleted} onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })} />
          </SimpleGrid>
          {saveSettings.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(saveSettings.error)}</Alert>}
          {perms.editAdmin && !isDeleted && (
            <Group justify="flex-end">
              <Button variant="default" disabled={!dirty || saveSettings.isPending} onClick={() => setForm(initial)}>Discard changes</Button>
              <Button leftSection={<IconDeviceFloppy size={16} />} disabled={!dirty} loading={saveSettings.isPending} onClick={() => saveSettings.mutate()}>Save membership</Button>
            </Group>
          )}
        </Stack>
      </Card>

      {/* Merge */}
      {perms.remove && !isDeleted && (
        <Card withBorder padding="lg" radius="md">
          <Stack gap="md">
            <div>
              <Group gap="xs"><IconGitMerge size={18} /><Text fw={600}>Merge a duplicate into this record</Text></Group>
              <Text size="sm" c="dimmed">
                {name} is kept. Empty fields here are filled from the duplicate, and its addresses, health, households, campus, notes and emergency contacts move across. The duplicate is then deleted.
              </Text>
            </div>
            <Group align="flex-end" wrap="wrap">
              <div style={{ flex: 1, minWidth: 280 }}>
                <MemberSearchSelect label="Duplicate record" value={other} onChange={setOther} excludeIds={[member.memberID]} />
              </div>
              <Button leftSection={<IconGitMerge size={16} />} disabled={!other} onClick={() => { setUnderstood(false); setMergeOpen(true); }}>Preview merge</Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* Delete / restore */}
      {perms.remove && (
        <Card withBorder padding="lg" radius="md" style={{ borderColor: 'var(--mantine-color-red-4)' }}>
          <Stack gap="sm">
            <Text fw={600} c="red">{isDeleted ? 'Deleted record' : 'Delete this record'}</Text>
            {isDeleted ? (
              <>
                <Text size="sm">
                  Deleted {formatStampTime(member.deletedDateTime)}.
                  {member.mergedIntoMemberID ? ' It was merged into another record and cannot be restored.' : ' Restoring brings it back into the directory and search.'}
                </Text>
                <Group>
                  {member.mergedIntoMemberID ? (
                    <Button variant="light" leftSection={<IconArrowRight size={16} />} onClick={() => navigate(`/admin/members/${member.mergedIntoMemberID}`)}>
                      Open the record it was merged into
                    </Button>
                  ) : (
                    <Button color="green" variant="light" leftSection={<IconRestore size={16} />} loading={restore.isPending} onClick={() => restore.mutate()}>Restore record</Button>
                  )}
                </Group>
                {restore.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(restore.error)}</Alert>}
              </>
            ) : (
              <>
                <Text size="sm">
                  The record is hidden from the directory, search and the login, but kept so history in other modules still shows a name. You can restore it later.
                </Text>
                {member.userId && (
                  <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                    This member is linked to a login. Unlink the login on the Links tab first, or merge this record into the right one.
                  </Alert>
                )}
                <Group>
                  <Button color="red" variant="light" leftSection={<IconTrash size={16} />} disabled={!!member.userId} onClick={() => setDeleteOpen(true)}>Delete record</Button>
                </Group>
              </>
            )}
          </Stack>
        </Card>
      )}

      {/* Merge preview */}
      <Modal opened={mergeOpen && !!other} onClose={() => setMergeOpen(false)} title="Merge preview" size="xl" centered>
        {other && (
          <Stack gap="md">
            <Group gap="xs" wrap="wrap">
              <Badge color="red" variant="light">Deleted: {memberFullName(other) || `Member ${other.memberID}`} (#{other.memberID})</Badge>
              <IconArrowRight size={16} />
              <Badge color="green" variant="light">Kept: {name} (#{member.memberID})</Badge>
            </Group>
            {bothHaveLogins && (
              <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Both records have a login">
                The merge will be refused. Unlink the login from one of them first.
              </Alert>
            )}
            <div style={{ overflowX: 'auto' }}>
            <Table tableVariant="striped" miw={560}>
              <Table.Thead>
                <Table.Tr><Table.Th>Field</Table.Th><Table.Th>This record</Table.Th><Table.Th>Duplicate</Table.Th><Table.Th>Result</Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {previewRows.map((r) => {
                  const fill = blank(r.mine) && !blank(r.theirs);
                  return (
                    <Table.Tr key={r.label}>
                      <Table.Td><Text size="sm" fw={500}>{r.label}</Text></Table.Td>
                      <Table.Td><Text size="sm" c={blank(r.mine) ? 'dimmed' : undefined}>{r.mine || 'empty'}</Text></Table.Td>
                      <Table.Td><Text size="sm" c={blank(r.theirs) ? 'dimmed' : undefined}>{r.theirs || 'empty'}</Text></Table.Td>
                      <Table.Td>{fill ? <Badge size="sm" color="blue" variant="light">Filled from duplicate</Badge> : <Text size="sm" c="dimmed">Kept</Text>}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            </div>
            <div>
              <Text size="sm" fw={600} mb={4}>Moves across from the duplicate</Text>
              {otherCounts.isLoading ? <Skeleton height={24} /> : (
                <Text size="sm" c="dimmed">
                  Addresses {count(otherCounts.data?.addresses)}, allergies {count(otherCounts.data?.allergies)}, disabilities {count(otherCounts.data?.disabilities)},
                  {' '}households {count(otherCounts.data?.families)}, campus rows {count(otherCounts.data?.locations)}, notes {count(otherCounts.data?.notes)},
                  {' '}emergency contacts {count(otherCounts.data?.emergency)}. Anything already on this record wins.
                </Text>
              )}
            </div>
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Other modules are not updated">
              Care, Courses, Departments, Forms, Locations, Projects, Carpool and Anatomy keep pointing at member #{other.memberID}. After the merge you get a checklist of modules to review.
            </Alert>
            <Checkbox label="I understand the duplicate is deleted and other modules must be checked by hand" checked={understood}
              onChange={(e) => setUnderstood(e.currentTarget.checked)} />
            {merge.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Not merged">{errorMessage(merge.error)}</Alert>}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setMergeOpen(false)}>Cancel</Button>
              <Button color="red" leftSection={<IconGitMerge size={16} />} disabled={!understood || bothHaveLogins} loading={merge.isPending} onClick={() => merge.mutate()}>
                Merge records
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Merge result + module checklist */}
      <Modal opened={!!result} onClose={() => setResult(null)} title="Merge complete" size="lg" centered>
        {result && (
          <Stack gap="md">
            <Alert color="green" icon={<IconCheck size={16} />}>
              Member #{result.mergedMemberID} was merged into #{result.memberID}.
              {result.fieldsFilled.length > 0 ? ` Filled: ${result.fieldsFilled.join(', ')}.` : ' No fields needed filling.'}
            </Alert>
            <Text size="sm">
              Moved: {result.moved.addresses} addresses, {result.moved.allergies} allergies, {result.moved.disabilities} disabilities, {result.moved.families} households,
              {' '}{result.moved.locations} campus rows, {result.moved.notes} notes and {result.moved.emergencyContacts} emergency contacts.
            </Text>
            <Divider label="Check these modules" labelPosition="left" />
            <Text size="sm" c="dimmed">They may still reference member #{result.mergedMemberID}. Tick each one once you have reviewed it.</Text>
            <Stack gap="xs">
              {result.modulesToCheck.map((m) => (
                <Checkbox key={m.module} checked={checked.includes(m.module)}
                  onChange={(e) => setChecked(e.currentTarget.checked ? [...checked, m.module] : checked.filter((x) => x !== m.module))}
                  label={<Text size="sm"><b>{m.module}</b>: {m.what}</Text>} />
              ))}
            </Stack>
            <Group justify="space-between">
              <Link to={`/admin/members/${result.mergedMemberID}`} style={{ fontSize: 14 }}>Open the merged record</Link>
              <Button onClick={() => setResult(null)} disabled={checked.length < result.modulesToCheck.length} variant={checked.length < result.modulesToCheck.length ? 'default' : 'filled'}>
                {checked.length < result.modulesToCheck.length ? `${result.modulesToCheck.length - checked.length} left to check` : 'Done'}
              </Button>
            </Group>
            {checked.length < result.modulesToCheck.length && (
              <Group justify="flex-end"><Button variant="subtle" size="xs" onClick={() => setResult(null)}>Close and check later</Button></Group>
            )}
          </Stack>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal opened={deleteOpen} onClose={() => setDeleteOpen(false)} title={`Delete ${name}?`} centered size="md">
        <Stack gap="md">
          <Text size="sm">The record is hidden from the directory and search. Nothing is erased, and you can restore it from this tab.</Text>
          {softDelete.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(softDelete.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteOpen(false)}>Keep it</Button>
            <Button color="red" leftSection={<IconTrash size={16} />} loading={softDelete.isPending} onClick={() => softDelete.mutate()}>Delete record</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
